import { Masonry } from "@mui/lab";
import { Box, MenuItem, TextField, useTheme } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjetosCamadasFormularios, Tabelas, ConfigCaracterizacao } from "../../../interfaces";
import { DashboardContext } from "../../../providers/Dashboard";
import { DebounceProvider } from "../../../providers/Debounce";
import { getStructure, getValue } from "../../../services/db";
import { getFormPreference, saveFormPreference } from "../../../services/utils";
import Input from "../Input";
import {
  buildSinterDesativacaoErrors,
  isCampoDesativacaoSinter,
  isStatusAtivo,
  isStatusInativo,
  SINTER_MOTIVO_DESATIVACAO_COLUNA,
  SINTER_STATUS_COLUNA,
  SINTER_TIPO_DESATIVACAO_COLUNA,
} from "../../../utils/sinterInativacao";

export default function Form(props: { formularios: ProjetosCamadasFormularios[]; tabela: Tabelas; configCaracterizacao?: ConfigCaracterizacao; integracaoSinter?: boolean; }) {
  const { metadata, setErrors } = useContext(DashboardContext);
  const theme = useTheme();

  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [sinterTick, setSinterTick] = useState(0);

  const masonryColumns = metadata.drawer
    ? { xs: 1, sm: 1, md: 3, lg: 4, xl: 6 }
    : { xs: 1, sm: 2, md: 3, lg: 6, xl: 8 };

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCurrentFormIndex(Number(e.target.value));
    saveFormPreference(props.tabela, e.target.value);
  }

  useEffect(() => {
    const form = getFormPreference(props.tabela);
    if (form) {
      setCurrentFormIndex(Number(form));
    }
  }, [props.tabela]);

  const currentForm = props.formularios[currentFormIndex] || null;
  const integracaoSinterAtiva = props.integracaoSinter === true || props.configCaracterizacao?.integracaoSinter === true;

  useEffect(() => {
    if (!integracaoSinterAtiva || props.tabela !== "imobiliario") return;

    const handler = (e: CustomEvent) => {
      const { tabela } = e.detail || {};
      if (tabela === "imobiliario") {
        setSinterTick((current) => current + 1);
      }
    };

    window.addEventListener("field-updated", handler as EventListener);
    return () => window.removeEventListener("field-updated", handler as EventListener);
  }, [integracaoSinterAtiva, props.tabela]);

  const camposNormalizados = useMemo(() => {
    if (!currentForm?.projetosCamadasFormulariosCampos) return [];
    void sinterTick;

    const structure = getStructure(props.tabela) || {};
    const imobiliarioId = metadata.id_imobiliario;
    const statusAtual =
      props.tabela === "imobiliario" && imobiliarioId
        ? getValue(imobiliarioId, "imobiliario", SINTER_STATUS_COLUNA)
        : undefined;
    const sinterStatusInativo = isStatusInativo(statusAtual);
    const sinterStatusAtivo = isStatusAtivo(statusAtual);

    return currentForm.projetosCamadasFormulariosCampos
      .filter((campo) => campo.ativo)
      .sort((a, b) => a.ordem - b.ordem)
      .map((campo) => {
        const campoNormalizado = { ...campo };
        const tblNomeColuna = campo.tblNomeColuna?.trim?.();

        if (!tblNomeColuna) {
          return campoNormalizado;
        }

        campoNormalizado.tblNomeColuna = tblNomeColuna;

        if (!Object.prototype.hasOwnProperty.call(structure, tblNomeColuna)) {
          console.warn(
            `O campo ${tblNomeColuna} não existe na structure da tabela ${props.tabela}. Ele será renderizado e adicionado localmente.`,
          );

          structure[tblNomeColuna] = metadata?.[tblNomeColuna] ?? null;
        }

        if (integracaoSinterAtiva && props.tabela === "imobiliario" && isCampoDesativacaoSinter(tblNomeColuna)) {
          campoNormalizado.obrigatorio = sinterStatusInativo;
          campoNormalizado.somenteVisualizacao = sinterStatusAtivo ? true : sinterStatusInativo ? false : campo.somenteVisualizacao;
        }

        return campoNormalizado;
      });
  }, [currentForm, props.tabela, metadata, integracaoSinterAtiva, sinterTick]);

  useEffect(() => {
    if (!integracaoSinterAtiva || props.tabela !== "imobiliario" || !metadata.id_imobiliario) return;

    const sinterErrors = buildSinterDesativacaoErrors({
      id: metadata.id_imobiliario,
      status: getValue(metadata.id_imobiliario, "imobiliario", SINTER_STATUS_COLUNA),
      tipo: getValue(metadata.id_imobiliario, "imobiliario", SINTER_TIPO_DESATIVACAO_COLUNA),
      motivo: getValue(metadata.id_imobiliario, "imobiliario", SINTER_MOTIVO_DESATIVACAO_COLUNA),
    });

    const storageErrors = JSON.parse(localStorage.errors || "{}");
    const nextErrors = { ...storageErrors };
    [
      `${metadata.id_imobiliario}@imobiliario@${SINTER_TIPO_DESATIVACAO_COLUNA}`,
      `${metadata.id_imobiliario}@imobiliario@${SINTER_MOTIVO_DESATIVACAO_COLUNA}`,
    ].forEach((key) => delete nextErrors[key]);

    const mergedErrors = { ...nextErrors, ...sinterErrors };
    localStorage.setItem("errors", JSON.stringify(mergedErrors));
    setErrors(mergedErrors);
  }, [integracaoSinterAtiva, metadata.id_imobiliario, props.tabela, setErrors, sinterTick]);

  useEffect(() => {
    if (!currentForm?.projetosCamadasFormulariosCampos) return;

    const storageKey = `structure@${props.tabela}`;
    const currentStructure = getStructure(props.tabela) || {};
    let hasChanges = false;

    const updatedStructure = { ...currentStructure };

    currentForm.projetosCamadasFormulariosCampos
      .filter((campo) => campo.ativo && campo.tblNomeColuna)
      .forEach((campo) => {
        const nomeColuna = campo.tblNomeColuna as string;

        if (!Object.prototype.hasOwnProperty.call(updatedStructure, nomeColuna)) {
          updatedStructure[nomeColuna] = metadata?.[nomeColuna] ?? null;
          hasChanges = true;
        }
      });

    if (hasChanges) {
      localStorage.setItem(storageKey, JSON.stringify(updatedStructure));
      console.info(`Structure da tabela ${props.tabela} atualizada com campos novos do formulário.`);
    }
  }, [currentForm, props.tabela, metadata]);

  return (
    <>
      <Box
        sx={{
          paddingTop: "1rem",
        }}
      >
        <TextField
          fullWidth
          select
          label="Selecione um formulário"
          value={props.formularios.length > 0 ? currentFormIndex : ""}
          defaultValue={0}
          onChange={handleFormChange}
          InputLabelProps={{
            sx: {
              "&.Mui-focused": {
                color: theme.palette.secondary.main,
              },
            },
          }}
        >
          {props.formularios.length > 0 ? (
            props.formularios.map((formulario, index) => (
              <MenuItem key={formulario.nome} value={index}>
                {formulario.nome}
              </MenuItem>
            ))
          ) : (
            <MenuItem value="" disabled>
              Nenhum formulário encontrado
            </MenuItem>
          )}
        </TextField>
      </Box>

      <Box
        sx={{
          paddingTop: "1rem",
        }}
      >
        <Masonry columns={masonryColumns} spacing={1} sequential>
          {camposNormalizados.map((campo, index) => {
            if (
              campo.tblNomeColuna === "id_bairro" ||
              campo.tblNomeColuna === "id_logradouro" ||
              campo.tblNomeColuna === "id_logradouro_tributario"
            ) {
              campo.tipo = 8;
              campo.tipoCampo = "8";
            }
            
            if (
              props.configCaracterizacao?.filtrarSecaoPorLogradouro &&
              props.tabela === "imobiliario" &&
              (campo.tblNomeColuna === "id_secao" ||
                campo.tblNomeColuna === "id_segmento_logradouro_tributario")
            ) {
              campo.tipo = 8;
              campo.tipoCampo = "8";
          
              const campoLogradouro =
                campo.tblNomeColuna === "id_segmento_logradouro_tributario"
                  ? "id_logradouro_tributario"
                  : "id_logradouro";

              campo.filtraCampo = campoLogradouro;
              campo.colunaFiltroRetorno = campoLogradouro;
            }
            
            return (
              <DebounceProvider key={index}>
                <Input
                  key={`${metadata[`id_${props.tabela}`]!}@${props.tabela}@${campo.tblNomeColuna}`}
                  tabela={props.tabela}
                  campo={campo}
                  integracaoSinter={integracaoSinterAtiva}
                />
              </DebounceProvider>
            );
          })}
        </Masonry>
      </Box>
    </>
  );
}
